import type { MeResponse, User } from "@/types/user";
import { useMutation, useQuery } from "@tanstack/vue-query";
import type { AccountDTO } from "~/types/account";
import ApiRoutes from "~/types/apiRoutes";
import { isSpecialSubdomain } from "~/utils/helper";

export const useAuth = defineStore("auth", () => {
  const { $axios, $reportError } = useNuxtApp();
  const config = useRuntimeConfig();
  const model_type = ref("");
  const subdomain = window.location.hostname.split(".").slice(0, -2).join(".");
  const host = window.location.host;
  const toast = useMessage();

  const account = ref<AccountDTO|null>(null);
  const user = ref<User | null>(null);
  const accounts = ref([]);
  const verification_error = ref(null);

  const {
    data: allAccounts,
    refetch: refetchAccounts,
    isSuccess: hasAllAccounts,
    error: errorAllAccounts,
  } = useQuery({
    queryKey: ["accounts"],
    queryFn: async () => {
      try {
        return await $axios.get(`/v1/accounts`).then((res) => res.data);
      } catch (error) {
        console.log(error);
        return error;
      }
    },
    enabled: computed(() => Boolean(user.value)),
  });

  watch(
    () => allAccounts.value,
    (value) => {
      accounts.value = value.data;
    },
  );

  const { data: accountData, refetch: refetchAccount } = useQuery({
    queryKey: ["account", subdomain],
    queryFn: async ({ queryKey }: any) => {
      try {
        return await $axios
          .get(`/v1/accounts/${queryKey[1]}`)
          .then((res) => res.data);
      } catch (error) {
        console.log(error);
        return error;
      }
    },
    enabled: computed(() =>
      Boolean(subdomain && !isSpecialSubdomain(subdomain)),
    ),
  });

  watch(
    () => accountData.value,
    (value) => {
      account.value = value;
    },
  );

  const {
    mutateAsync: refetch,
    isSuccess,
    isPending,
  } = useMutation({
    mutationFn: async () =>
      await $axios.get<MeResponse>("/user").then((res) => res.data),
    onSuccess: (data) => {
      try {
        if (!data.user || !data.type) {
          throw new Error('The user information was not loaded correctly.');
        }
        
        // Assign it if it has a value.
        model_type.value = data.type;
        user.value = data.user ?? null;
      } catch (error) {
        $reportError(error);
        toast.message({
          title: "Error",
          message: "An unexpected error occurred trying to load the user information.",
        });
        setTimeout(() => {
          navigateTo(`${config.public.primaryURL}/login`, {
            external: true,
          });
        }, 2000);
      }
    },
    onError: (error) => {
      user.value = null;
      console.log(error);
    },
  });

  const { mutateAsync: logout } = useMutation({
    mutationFn: () => $axios.post("/logout"),
    onSuccess: async () => {
      user.value = null;
      isSuccess.value = false; // isSuccess controls if user is logged in.
      navigateTo(`https://app.${config.public.domain}/login`, { external: true });
    },

    onError: (error) => {
      console.log(error);
    },
  });

  const { mutateAsync: logoutNoRoute, isSuccess: isLoggedOutRoute } =
    useMutation({
      mutationFn: (_data) => $axios.post("/logout"),
      onSuccess: () => {
        user.value = null;
        isSuccess.value = false;
      },
      onError: (error) => {
        console.log(error);
      },
    });

    /**
     * This method updates the account settings in the backend.
     */
    const { mutateAsync: updateAccountSettings } =
      useMutation({
        mutationFn: (data: Record<string, any>) => $axios.post(ApiRoutes.ACCOUNT_UPDATE_SETTINGS, data, {headers: {Accept: 'text/html'}}),
        onSuccess: (response) => {
          if (response?.status == 200 && response?.data?.status == 'success') {
            toast.message({ message: response?.data?.message, type: "success", title: "Account Settings Updated" });
            refetchAccount();
          }
        },
        onError: (error: any) => {
          $reportError(error);
          if (error?.response?.data?.message) {
              toast.message({ message: error?.response?.data?.message, type: "error", title: "Error Updating Account Settings" });
          }
        },
    });

  /**
   * Verify if the user has the owner role. This means they are the user of the account.
   */
  const isOwner = (): boolean => {
    return user.value?.roles?.some((role: any) => role.name === 'Owner') ?? false;
  }

  /**
   * Verify if the user has the admin role. This means they are a system admin, independent of the account.
   */
  const isAdmin = (): boolean => {
    return user.value?.roles?.some((role: any) => role.name === 'admin') ?? false;
  }

  /**
   * This method sets the suspended status for the account.
   */
  const setSuspendedStatus = () => {
    if (account.value) {
      account.value.account_status = 'SUSPENDED';
    }
  }

  return {
    host,
    accounts,
    refetchAccounts,
    isLoggedOutRoute,
    refetchAccount,
    logoutNoRoute,
    subdomain,
    account,
    model_type,
    user,
    isPending,
    refetch,
    isSuccess,
    logout,
    verification_error,
    updateAccountSettings,
    isOwner,
    isAdmin,
    setSuspendedStatus,
  };
});
